import React, { useState } from 'react';
import { CopyButtonProps } from '../types';

function CopyButton({ text, small }: CopyButtonProps): React.ReactElement {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    const showCopied = (): void => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    };

    // Preferred path: async Clipboard API (requires a secure context).
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showCopied();
        return;
      }
    } catch {
      // Fall through to the legacy fallback below.
    }

    // Fallback for in-app browsers (Instagram / Facebook / TikTok webviews) where
    // navigator.clipboard is undefined — that's most of the site's mobile traffic.
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      document.execCommand('copy');
      document.body.removeChild(ta);
      showCopied();
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <button
      className={['copy-btn', isCopied && 'copied', small && 'copy-btn-small'].filter(Boolean).join(' ')}
      onClick={handleCopy}
      aria-label={isCopied ? 'Copied!' : 'Copy to clipboard'}
    >
      {isCopied ? (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
            <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

export default CopyButton;
